import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Sparkles, Send, Trash2, Loader2, Mail, X, Eye, Save, CheckCircle2, Wand2, Settings } from "lucide-react";

const input = "w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const { user } = useAuth();

  const load = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  useEffect(() => {
    load();
    api.get("/admin/webinars").then((r) => setWebinars(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    const res = await api.post("/admin/campaigns", { name: "Untitled Campaign", subject: "", html_content: "" });
    setEditing(res.data);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/admin/campaigns/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8" data-testid="campaigns-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Email campaigns</h1>
          <p className="text-[#4B5563] mt-1">Let AI write & design your emails, then send via Brevo.</p>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <button onClick={() => setShowSettings(true)} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2.5 font-semibold flex items-center gap-2" data-testid="writing-rules-btn">
              <Settings className="w-4 h-4" /> Writing rules
            </button>
          )}
          <button onClick={create} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] px-5 py-2.5 font-semibold flex items-center gap-2" data-testid="create-campaign-btn">
            <Plus className="w-4 h-4" /> New campaign
          </button>
        </div>
      </div>

      {campaigns === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] bg-white p-16 text-center" data-testid="campaigns-empty">
          <p className="text-[#4B5563] font-medium mb-4">No campaigns yet.</p>
          <button onClick={create} className="bg-[#6D28B5] text-white px-5 py-2.5 font-semibold inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create your first campaign
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]">
          {campaigns.map((c, i) => (
            <div key={c.id} className={`flex items-center gap-4 p-4 ${i > 0 ? "border-t border-[#E5E7EB]" : ""}`} data-testid={`campaign-row-${c.id}`}>
              <div className="w-10 h-10 bg-[#F3F4F6] border border-[#E5E7EB] flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[#6D28B5]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-xs text-[#4B5563] truncate">{c.subject || "No subject yet"}</div>
              </div>
              {c.status === "sent" ? (
                <span className="text-xs font-bold uppercase tracking-wide text-[#10B981] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Sent to {c.sent_count}</span>
              ) : (
                <span className="text-xs font-bold uppercase tracking-wide text-[#9CA3AF]">Draft</span>
              )}
              <button onClick={() => setEditing(c)} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-3 py-1.5 text-sm font-semibold" data-testid={`edit-campaign-${c.id}`}>Open</button>
              <button onClick={() => remove(c.id)} className="p-2 hover:bg-red-50"><Trash2 className="w-4 h-4 text-[#FF3B30]" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CampaignEditor
          campaign={editing}
          webinars={webinars}
          isHost={user?.role !== "admin"}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}

function SettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => setS({ writing_rules: "", default_sender_email: "", default_sender_name: "" }));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
      onClose();
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="settings-modal">
      <div className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="font-heading font-black text-xl tracking-tighter">Email & writing rules</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F3F4F6]"><X className="w-5 h-5" /></button>
        </div>
        {!s ? (
          <div className="p-8 text-[#9CA3AF]">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">AI writing rules</label>
              <textarea
                value={s.writing_rules || ""}
                onChange={(e) => setS({ ...s, writing_rules: e.target.value })}
                rows={6}
                placeholder={"Things AI must avoid. e.g.\nNever use em-dashes.\nAvoid the words: unlock, elevate, dive in.\nKeep sentences short and warm."}
                className={input}
                data-testid="writing-rules-input"
              />
              <p className="text-xs text-[#6B7280] mt-1.5">Applied to every email AI writes. Em-dashes are also auto-removed when you mention them here.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Default sender email</label>
                <input value={s.default_sender_email || ""} onChange={(e) => setS({ ...s, default_sender_email: e.target.value })} placeholder="happiness@yourdomain.com" className={input} data-testid="default-sender-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Default sender name</label>
                <input value={s.default_sender_name || ""} onChange={(e) => setS({ ...s, default_sender_name: e.target.value })} placeholder="Upward Cast" className={input} />
              </div>
            </div>
            <p className="text-xs text-[#6B7280]">Used when an experience has no sender set. Must be a verified Brevo sender.</p>
            <button onClick={save} disabled={saving} className="w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-6 py-3 font-semibold flex items-center justify-center gap-2" data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignEditor({ campaign, webinars, isHost, onClose }) {
  const [c, setC] = useState(campaign);
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("professional");
  const [webinarId, setWebinarId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendWebinarId, setSendWebinarId] = useState("");
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    if (isHost && webinars.length) {
      if (!webinarId) setWebinarId(webinars[0].id);
      if (!sendWebinarId) setSendWebinarId(webinars[0].id);
    }
  }, [isHost, webinars, webinarId, sendWebinarId]);

  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe what the email should say");
    setGenerating(true);
    try {
      const res = await api.post("/admin/campaigns/generate", { prompt, tone, webinar_id: webinarId || null });
      set({ html_content: res.data.html_content, subject: res.data.subject || c.subject });
      toast.success("Email drafted by AI");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    }
    setGenerating(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/campaigns/${c.id}`, { name: c.name, subject: c.subject, html_content: c.html_content });
      setC(res.data);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const send = async (test) => {
    if (!c.html_content) return toast.error("Generate or write the email first");
    if (!test && isHost && !sendWebinarId) return toast.error("Choose which experience this campaign goes to");
    setSending(true);
    try {
      await api.put(`/admin/campaigns/${c.id}`, { name: c.name, subject: c.subject, html_content: c.html_content });
      const body = test ? { test_email: testEmail } : { webinar_id: sendWebinarId || null };
      const res = await api.post(`/admin/campaigns/${c.id}/send`, body);
      toast.success(test ? `Test sent to ${testEmail}` : `Sent to ${res.data.sent} recipients`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
    }
    setSending(false);
  };

  const selectedWebinar = webinars.find((w) => w.id === sendWebinarId);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" data-testid="campaign-editor">
      <div className="bg-white w-full max-w-5xl h-full overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between z-10">
          <input value={c.name} onChange={(e) => set({ name: e.target.value })} className="font-heading font-black text-xl tracking-tighter bg-transparent focus:outline-none" data-testid="campaign-name-input" />
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button onClick={onClose} className="p-2 hover:bg-[#F3F4F6]" data-testid="close-campaign-btn"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100%-65px)]">
          {/* Left: controls */}
          <div className="p-6 space-y-6 border-r border-[#E5E7EB]">
            <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-5">
              <div className="flex items-center gap-2 font-bold mb-3"><Wand2 className="w-4 h-4 text-[#6D28B5]" /> Write with AI</div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder="e.g. Invite registrants to my free masterclass on scaling agencies. Emphasize the live Q&A and limited seats."
                className={input}
                data-testid="ai-prompt-input"
              />
              <div className="grid grid-cols-2 gap-2 mt-3">
                <select value={tone} onChange={(e) => setTone(e.target.value)} className={input}>
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="urgent">Urgent / FOMO</option>
                  <option value="playful">Playful</option>
                </select>
                <select value={webinarId} onChange={(e) => setWebinarId(e.target.value)} className={input} data-testid="campaign-webinar-select">
                  {!isHost && <option value="">No experience context</option>}
                  {webinars.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                </select>
              </div>
              <button onClick={generate} disabled={generating} className="mt-3 w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2.5 font-semibold flex items-center justify-center gap-2" data-testid="generate-email-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate email
              </button>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">Subject line</label>
              <input value={c.subject} onChange={(e) => set({ subject: e.target.value })} className={input} data-testid="campaign-subject-input" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">HTML content</label>
              <textarea value={c.html_content} onChange={(e) => set({ html_content: e.target.value })} rows={8} className={`${input} font-mono text-xs`} data-testid="campaign-html-input" />
            </div>

            {/* Send */}
            <div className="border-t border-[#E5E7EB] pt-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">
                  Send to {isHost && <span className="text-[#FF3B30]">*</span>}
                </label>
                <select value={sendWebinarId} onChange={(e) => setSendWebinarId(e.target.value)} className={input} data-testid="campaign-send-webinar-select">
                  {!isHost && <option value="">Everyone (all registrants, across every experience)</option>}
                  {isHost && webinars.length === 0 && <option value="">Create an experience first</option>}
                  {webinars.map((w) => <option key={w.id} value={w.id}>{w.title} — registrants only</option>)}
                </select>
                <p className="text-xs text-[#6B7280] mt-1.5">
                  {sendWebinarId
                    ? "Goes only to people registered for this experience."
                    : "Goes to everyone who has registered for any experience (each person once)."}
                </p>
              </div>
              <div className="flex gap-2">
                <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@email.com" className={input} data-testid="test-email-input" />
                <button onClick={() => send(true)} disabled={sending || !testEmail} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 text-sm font-semibold shrink-0 disabled:opacity-50 flex items-center gap-1.5" data-testid="send-test-btn">
                  <Send className="w-4 h-4" /> Test
                </button>
              </div>
              <button onClick={() => send(false)} disabled={sending || (isHost && !sendWebinarId)} className="w-full bg-[#FF3B30] text-white hover:bg-[#CC2F26] disabled:opacity-50 px-4 py-3 font-bold uppercase tracking-wide text-sm flex items-center justify-center gap-2" data-testid="send-campaign-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendWebinarId ? `Send to ${selectedWebinar ? selectedWebinar.title : "experience"} registrants` : "Send to everyone"}
              </button>
            </div>
          </div>

          {/* Right: preview */}
          <div className="bg-[#F3F4F6]">
            <div className="px-6 py-3 border-b border-[#E5E7EB] flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#4B5563] bg-white">
              <Eye className="w-4 h-4" /> Preview
            </div>
            <div className="p-4">
              {c.html_content ? (
                <iframe title="preview" srcDoc={c.html_content} className="w-full h-[600px] bg-white border border-[#E5E7EB]" data-testid="email-preview" />
              ) : (
                <div className="h-[600px] flex items-center justify-center text-[#9CA3AF] text-sm">Your email preview appears here.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
